/**
 * TanStack Query hooks for suggestions management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { eventService } from '../services/eventService'
import { transformSuggestion } from '../services/transformers'
import type { Suggestion } from '../types/mockup'

/**
 * Fetch suggestions for an event with optional filters
 */
export const useSuggestions = (
  eventId: string,
  filters?: {
    status?: string
    risk_level?: string
    confidence_min?: number
    confidence_max?: number
  }
) => {
  return useQuery({
    queryKey: ['event', eventId, 'suggestions', filters],
    queryFn: async () => {
      const data = await eventService.getSuggestions(eventId, filters)
      // Transform dates in suggestions
      return data.map((s: any) => transformSuggestion(s))
    },
    enabled: !!eventId,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute for real-time updates
  })
}

/**
 * Fetch a single suggestion
 */
export const useSuggestion = (eventId: string, suggestionId: string) => {
  return useQuery({
    queryKey: ['event', eventId, 'suggestion', suggestionId],
    queryFn: async () => {
      const data = await eventService.getSuggestion(eventId, suggestionId)
      return transformSuggestion(data)
    },
    enabled: !!eventId && !!suggestionId,
    staleTime: 30000,
  })
}

/**
 * Approve a suggestion with optimistic updates
 */
export const useApproveSuggestion = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ eventId, suggestionId }: { eventId: string; suggestionId: string }) => {
      return eventService.approveSuggestion(eventId, suggestionId)
    },

    // Optimistic update
    onMutate: async ({ eventId, suggestionId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['event', eventId, 'suggestions'] })

      // Snapshot previous values
      const previousSuggestions = queryClient.getQueryData(['event', eventId, 'suggestions'])

      // Optimistically update suggestion status
      queryClient.setQueryData(['event', eventId, 'suggestions'], (old: any) => {
        if (!old) return old
        return old.map((s: Suggestion) =>
          s.id === suggestionId ? { ...s, status: 'applied', actionAt: new Date() } : s
        )
      })

      // Return context for rollback
      return { previousSuggestions, eventId }
    },

    // On error, rollback
    onError: (err, variables, context) => {
      if (context) {
        queryClient.setQueryData(
          ['event', context.eventId, 'suggestions'],
          context.previousSuggestions
        )
      }
    },

    // Always refetch after error or success
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['event', variables.eventId, 'suggestions'] })
      queryClient.invalidateQueries({ queryKey: ['event', variables.eventId] })
    },
  })
}

/**
 * Reject a suggestion with optimistic updates
 */
export const useRejectSuggestion = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ eventId, suggestionId }: { eventId: string; suggestionId: string }) => {
      return eventService.rejectSuggestion(eventId, suggestionId)
    },

    // Optimistic update
    onMutate: async ({ eventId, suggestionId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['event', eventId, 'suggestions'] })

      // Snapshot previous values
      const previousSuggestions = queryClient.getQueryData(['event', eventId, 'suggestions'])

      // Optimistically update suggestion status
      queryClient.setQueryData(['event', eventId, 'suggestions'], (old: any) => {
        if (!old) return old
        return old.map((s: Suggestion) =>
          s.id === suggestionId ? { ...s, status: 'dismissed', actionAt: new Date() } : s
        )
      })

      // Return context for rollback
      return { previousSuggestions, eventId }
    },

    // On error, rollback
    onError: (err, variables, context) => {
      if (context) {
        queryClient.setQueryData(
          ['event', context.eventId, 'suggestions'],
          context.previousSuggestions
        )
      }
    },

    // Always refetch after error or success
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['event', variables.eventId, 'suggestions'] })
    },
  })
}

/**
 * Batch approve multiple suggestions
 */
export const useBatchApproveSuggestions = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      eventId,
      suggestionIds,
    }: {
      eventId: string
      suggestionIds: string[]
    }) => {
      // Apply each suggestion sequentially
      const results = []
      for (const id of suggestionIds) {
        const result = await eventService.approveSuggestion(eventId, id)
        results.push(result)
      }
      return results
    },

    onSuccess: (data, variables) => {
      // Refresh all data
      queryClient.invalidateQueries({ queryKey: ['event', variables.eventId, 'suggestions'] })
      queryClient.invalidateQueries({ queryKey: ['event', variables.eventId] })
    },
  })
}

